import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import type { PrimaryNavigationLayoutScreen } from "@-/dev.improve.haniverse.routes.nav.entities";
import type { RoutePath } from "@-/com.npmjs.expo-router";
import { useMemo } from "react";
import { PrimaryNavigationStripDestinationIcon } from "#primary-navigation-strip-destination-icon";
import { requestBrowseReset } from "#request-browse-reset";
import { requestLearnCards } from "#request-learn-cards";
import { requestStatsScrollToTop } from "#request-stats-scroll-to-top";
import {
  PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS,
  usePrimaryNavigationStripDestinations,
} from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-customization";

/**
 * Learn's tab while a new learner's onboarding presents it as the map (`lib/onboarding`): the map is
 * Learn's own mode (`/learn/map`), so the one tab shows and opens the map until the onboarding
 * turns it into Learn.
 */
const LEARN_PRESENTED_AS_MAP_TAB: NonNullable<PrimaryNavigationLayoutScreen["tab"]> = {
  dismissToOnPrimaryNavigationPress: "/(nav)/(app)/learn/map" as RoutePath,
  icon: () => <PrimaryNavigationStripDestinationIcon destination="map" tabName="learn" />,
  tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.map.label,
  title: "Map",
};

/** A strip destination's screen name: the map is Learn's tab. */
function getStripScreenName(destination: PrimaryNavigationDestination): string {
  return destination === "map" ? "learn" : destination;
}

/**
 * The strip's tab order as screen names (relative to the app base), for the layout's
 * `stripOrder`.
 */
export function usePrimaryNavigationStripOrder(): readonly string[] {
  const stripDestinations = usePrimaryNavigationStripDestinations();
  return useMemo(
    () => stripDestinations.map((destination) => getStripScreenName(destination)),
    [stripDestinations],
  );
}

/**
 * Every app destination's screen, in a fixed registration order. Only the saved strip
 * destinations carry a tab; pass `usePrimaryNavigationStripDestinations()` as the layout's
 * `stripOrder` to show them in the user's order.
 */
export function usePrimaryNavigationStripScreens(): PrimaryNavigationLayoutScreen[] {
  const stripDestinations = usePrimaryNavigationStripDestinations();
  const screens = useMemo(
    (): PrimaryNavigationLayoutScreen[] => [
      {
        route: "/(nav)/(app)/stats",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/stats" as RoutePath,
          icon: () => <PrimaryNavigationStripDestinationIcon destination="stats" />,
          onFocusedPrimaryNavigationPress: requestStatsScrollToTop,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.stats.label,
          title: "Stats",
        },
      },
      {
        route: "/(nav)/(app)/decks",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/decks/today",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="decks" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.decks.label,
          title: "Decks",
        },
      },
      {
        route: "/(nav)/(app)/learn",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/learn/summary" as RoutePath,
          icon: () => <PrimaryNavigationStripDestinationIcon destination="learn" />,
          // On Learn's map (where the app opens) the tab leaves it for the cards.
          onFocusedPrimaryNavigationPress: requestLearnCards,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.learn.label,
          title: "Learn",
        },
      },
      {
        // Browse tab root is a Stack; typed routes omit this segment but pathname still starts with it.
        route: "/(nav)/(app)/browse",
        tab: {
          // The tab presentation remains "Cards", while its a11y/matchable name is "Browse" and
          // its root nested screen publishes "Levels" into the shared app header.
          accessibilityLabel: "Browse",
          // Target the stack root itself, never `/browse`: that resolves to the `index` redirect
          // route, which is not in the stack once it has redirected, so `POP_TO` would replace the
          // top route and slide a fresh redirect page in from the right instead of popping back.
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/browse/(browse-tabs)/levels",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="browse" />,
          onAfterDismissOnPrimaryNavigationPress: requestBrowseReset,
          optimisticPrimaryControlsStripTitle: "Levels",
          preserveBaseSceneOnPrimaryNavigationPress: true,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.browse.label,
          title: "Cards",
        },
      },
      {
        route: "/(nav)/(app)/search" as RoutePath,
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/search" as RoutePath,
          icon: () => <PrimaryNavigationStripDestinationIcon destination="search" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.search.label,
          title: "Search",
        },
      },
      {
        route: "/(nav)/(app)/idioms",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/idioms",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="idioms" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.idioms.label,
          title: "Idioms",
        },
      },
      {
        route: "/(nav)/(app)/cookbook",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/cookbook",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="cookbook" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.cookbook.label,
          title: "Cookbook",
        },
      },
      {
        route: "/(nav)/(app)/collections",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/collections",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="collections" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.collections.label,
          title: "Collections",
        },
      },
      {
        route: "/(nav)/(app)/settings",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/settings",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="settings" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.settings.label,
          title: "Settings",
        },
      },
      {
        route: "/(nav)/(app)/changelog",
        tab: {
          dismissToOnPrimaryNavigationPress: "/(nav)/(app)/changelog",
          icon: () => <PrimaryNavigationStripDestinationIcon destination="changelog" />,
          tabBarLabel: PRIMARY_NAVIGATION_DESTINATION_PRESENTATIONS.changelog.label,
          title: "Changelog",
        },
      },
    ],
    [],
  );
  // Keep every route mounted; only hide its tab button. The strip destinations already exclude
  // gated ones (Cookbook is experimental) and the hidden Idioms / Collections, which stay
  // reachable as admin-only openers (Map is Learn's own mode now, with no route of its own).
  return useMemo(
    () =>
      screens.map((screen) => {
        if (screen.route === "/(nav)/(app)/learn" && stripDestinations.includes("map")) {
          return { ...screen, tab: LEARN_PRESENTED_AS_MAP_TAB };
        }
        return {
          ...screen,
          tab: stripDestinations.some(
            (name) => screen.route === `/(nav)/(app)/${getStripScreenName(name)}`,
          )
            ? screen.tab
            : null,
        };
      }),
    [screens, stripDestinations],
  );
}
